/**
 * Open sub-account — two-spine spillover only (left 2.1 bottom / right 2.2 then 2.1).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { subAccountsAPI } from '../api/sub-accounts';
import { useAuthStore } from '../store/auth';
import { TwoSpinePlacementPicker } from '../components/TwoSpinePlacementPicker';
import type { PlacementSelection } from '../components/PlacementTreePicker';
import {
  ReadonlyFormField,
  StarPackageSelect,
  subPaymentFormula,
  type RegisterStarPackage,
} from '../components/global-players/GlobalTableRegistrationForm';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedNotice,
  ThemedPanel,
  ThemedSubmitButton,
} from '../components/ui/ThemedSurface';
import { TransactionSecurity } from '../components/ui/TransactionSecurity';

type ReinvestCoin = 'sp1' | 'sp3';

interface OpenSubPreview {
  master: { id: number; username: string; level_code: string };
  referrer?: {
    id: number;
    username: string;
    player_number: string;
    nickname: string;
  };
  next_username: string;
  zone: 'L' | 'R';
  zone_label: string;
  placement: {
    id: number;
    username: string;
    player_number?: string;
    nickname?: string;
  };
  leg_performance: { left: string; right: string };
  star_package: RegisterStarPackage;
  e3_enabled?: boolean;
  available_packages?: RegisterStarPackage[];
  sub_count: number;
  reinvest_coin_type: ReinvestCoin;
  pay: { coin: ReinvestCoin; coin_amount: string; tp_amount: string; fc_amount: string };
  required: { coin: ReinvestCoin; coin_amount: string; tp: string; fc: string };
  balances: { coin: string; tp: string; fc: string };
  sufficient: boolean;
}

export function OpenSubAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile) as Record<string, unknown> | null;

  const rootUserId = profile?.id as number | undefined;
  const rootUsername = (profile?.username as string | undefined) ?? '—';

  const urlPosition = useMemo(() => {
    const pos = (searchParams.get('position') ?? searchParams.get('zone') ?? '').toUpperCase();
    return pos === 'R' ? ('R' as const) : pos === 'L' ? ('L' as const) : undefined;
  }, [searchParams]);

  const [selection, setSelection] = useState<PlacementSelection | null>(null);
  const [starPackage, setStarPackage] = useState<RegisterStarPackage>('E2');
  const [reinvestCoin, setReinvestCoin] = useState<ReinvestCoin>('sp1');
  const [txPassword, setTxPassword] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [showSecurity, setShowSecurity] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const previewQuery = useQuery({
    queryKey: ['sub-accounts', 'open-preview', rootUserId, starPackage, reinvestCoin],
    queryFn: () =>
      subAccountsAPI.openPreview({
        star_package: starPackage,
        reinvest_coin_type: reinvestCoin,
      }) as Promise<OpenSubPreview>,
    enabled: !!rootUserId,
  });

  const preview = previewQuery.data;
  const availablePackages: RegisterStarPackage[] = preview?.available_packages?.length
    ? preview.available_packages
    : ['E1', 'E2'];

  useEffect(() => {
    if (!availablePackages.includes(starPackage)) {
      setStarPackage(availablePackages.includes('E2') ? 'E2' : availablePackages[0]!);
    }
  }, [availablePackages, starPackage]);

  useEffect(() => {
    if (selection || !rootUserId || !urlPosition) return;
    setSelection({
      userId: rootUserId,
      username: rootUsername,
      position: urlPosition,
    });
  }, [rootUserId, rootUsername, selection, urlPosition]);

  const previewErrorText =
    previewQuery.isError && previewQuery.error instanceof ApiError
      ? previewQuery.error.message
      : previewQuery.isError
        ? t('openSubAccount.previewFailed', '无法加载开通预览，请稍后重试')
        : null;

  const openMut = useMutation({
    mutationFn: () =>
      subAccountsAPI.open({
        reinvest_coin_type: reinvestCoin,
        transaction_password: txPassword,
        security_answer: securityAnswer,
        position: selection!.position,
        placement_user_id: selection!.userId,
        star_package: starPackage,
      }) as Promise<{ username?: string }>,
    onSuccess: (data: { username?: string }) => {
      setMsg({
        type: 'ok',
        text: t('openSubAccount.success', {
          username: data?.username ?? preview?.next_username ?? '—',
          defaultValue: `子账户 ${data?.username ?? ''} 已开通`,
        }),
      });
      void qc.invalidateQueries({ queryKey: ['sub-accounts'] });
      void qc.invalidateQueries({ queryKey: ['balances'] });
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['binary-tree'] });
      setTimeout(() => navigate('/sub-accounts', { replace: true }), 1800);
    },
    onError: (e: unknown) => {
      setMsg({
        type: 'err',
        text: e instanceof ApiError ? e.message : t('openSubAccount.failed', 'Failed to open sub-account'),
      });
    },
  });

  const handleSubmit = () => {
    setMsg(null);
    if (!selection) {
      setMsg({ type: 'err', text: t('registerMember.pickPlacement', '请先在网体上选择安置位置') });
      return;
    }
    if (!preview?.sufficient) {
      setMsg({ type: 'err', text: t('openSubAccount.insufficient', '支付币种 / TP / FC 余额不足') });
      return;
    }
    openMut.mutate();
  };

  const payCoin = (preview?.required.coin ?? reinvestCoin).toUpperCase();
  const requiredCoin = preview?.required.coin_amount ?? '—';
  const requiredTp = preview?.required.tp ?? '0';
  const requiredFc = preview?.required.fc ?? '0';
  const balanceCoin = preview?.balances.coin ?? '0';
  const balanceTp = preview?.balances.tp ?? '0';
  const balanceFc = preview?.balances.fc ?? '0';
  const hasTpLeg = Number(requiredTp) > 0;
  const payLabel = hasTpLeg
    ? `支付 ${requiredCoin} ${payCoin} + ${requiredTp} TP + ${requiredFc} FC 并开子账户`
    : `支付 ${requiredCoin} ${payCoin} + ${requiredFc} FC 并开子账户`;

  const securityQuestion = profile?.security_question as string | undefined;
  const questionText = securityQuestion ?? t('convert.defaultSecurityQuestion');

  const referrerPlayerNumber =
    preview?.referrer?.player_number ??
    (profile?.player_number as string | undefined)?.trim() ??
    String(rootUserId ?? '—');
  const referrerNickname =
    preview?.referrer?.nickname ??
    (profile?.nickname as string | undefined)?.trim() ??
    rootUsername;

  const fmtPerf = (raw: string | undefined) => {
    const n = Number(raw ?? '0');
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
      <ThemedPageHeader title={t('openSubAccount.title', 'Open Sub-account')} onBack={() => navigate(-1)} theme={C} />

      <main className="space-y-4 px-4 pb-28 pt-[70px]">
        {previewErrorText && <ThemedNotice tone="error">{previewErrorText}</ThemedNotice>}

        <ThemedPanel className="p-4">
          <p className="text-xs leading-relaxed" style={{ color: C.muted }}>
            {t(
              'openSubAccount.subtitle',
              '选择支付币种在主号下创建子账户（SP1 按 95%+5%TP，SP3 按 90%+10%TP，另收 0.05 FC）；只能挂登录号的左 2.1 底或右 2.2→2.1 底。',
            )}
          </p>
        </ThemedPanel>

        {rootUserId ? (
          <ThemedPanel className="p-4">
            <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
              {t('openSubAccount.placementTitle', '安置与账号')}
            </div>
            {preview && (
              <div className="mb-4 space-y-3">
                <ReadonlyFormField
                  label={t('registerForm.referrerId', '推荐人ID')}
                  value={referrerPlayerNumber}
                  hint={`${t('registerForm.referrerNickname', '推荐人昵称')}:${referrerNickname}`}
                />
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                    <div style={{ color: C.muted }}>{t('openSubAccount.nextUsername', 'Sub username')}</div>
                    <div className="mt-1 font-mono font-semibold" style={{ color: C.gold }}>
                      {preview.next_username}
                    </div>
                  </div>
                  <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                    <div style={{ color: C.muted }}>{t('openSubAccount.leftPerf', '左区业绩')}</div>
                    <div className="mt-1 font-mono" style={{ color: C.text }}>
                      {fmtPerf(preview.leg_performance.left)}
                    </div>
                  </div>
                  <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                    <div style={{ color: C.muted }}>{t('openSubAccount.rightPerf', '右区业绩')}</div>
                    <div className="mt-1 font-mono" style={{ color: C.text }}>
                      {fmtPerf(preview.leg_performance.right)}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <TwoSpinePlacementPicker
              rootUserId={rootUserId}
              rootUsername={rootUsername}
              selection={selection}
              onSelect={setSelection}
            />
          </ThemedPanel>
        ) : null}

        {preview && (
          <ThemedPanel className="p-4">
            <div className="mb-3 text-sm font-semibold" style={{ color: C.text }}>
              {t('openSubAccount.costTitle', '开通费用')}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                <div style={{ color: C.muted }}>{payCoin}</div>
                <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                  {requiredCoin}
                </div>
                <div className="mt-1" style={{ color: C.muted }}>
                  {t('openSubAccount.balance', '余额')}: {Number(balanceCoin).toFixed(2)}
                </div>
              </div>
              {hasTpLeg && (
                <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                  <div style={{ color: C.muted }}>TP</div>
                  <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                    {requiredTp}
                  </div>
                  <div className="mt-1" style={{ color: C.muted }}>
                    {t('openSubAccount.balance', '余额')}: {Number(balanceTp).toFixed(2)}
                  </div>
                </div>
              )}
              <div className="rounded-xl border px-3 py-2" style={{ borderColor: C.line }}>
                <div style={{ color: C.muted }}>FC</div>
                <div className="mt-1 font-mono text-base font-semibold" style={{ color: C.gold }}>
                  {requiredFc}
                </div>
                <div className="mt-1" style={{ color: C.muted }}>
                  {t('openSubAccount.balance', '余额')}: {Number(balanceFc).toFixed(2)}
                </div>
              </div>
            </div>
            {!preview.sufficient && (
              <ThemedNotice tone="error">
                {t('openSubAccount.insufficient', '支付币种 / TP / FC 余额不足')}
              </ThemedNotice>
            )}
          </ThemedPanel>
        )}

        <ThemedPanel className="space-y-4 p-4">
          <StarPackageSelect
            label={t('registerForm.starLevel', '玩家星级')}
            value={starPackage}
            available={availablePackages}
            onChange={setStarPackage}
          />

          <div>
            <div className="mb-2 text-xs font-medium" style={{ color: C.muted }}>
              {t('openSubAccount.reinvestType', 'Reinvest coin type')} <span className="text-negative">*</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(['sp1', 'sp3'] as const).map((coin) => {
                const active = reinvestCoin === coin;
                return (
                  <button
                    key={coin}
                    type="button"
                    onClick={() => setReinvestCoin(coin)}
                    className="rounded-xl border px-2 py-3 text-sm font-semibold uppercase transition active:scale-[0.98]"
                    style={{
                      borderColor: active ? `${C.gold}88` : C.line,
                      background: active ? `${C.gold}14` : C.inputBg,
                      color: active ? C.gold : C.muted,
                    }}
                  >
                    {coin.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[11px]" style={{ color: C.muted }}>
              {subPaymentFormula(reinvestCoin)}
            </p>
          </div>

          <p className="text-[11px] leading-relaxed" style={{ color: C.muted }}>
            {t('openSubAccount.noSubLoginPassword')}
          </p>

          <TransactionSecurity
            txPassword={txPassword}
            onTxPasswordChange={setTxPassword}
            showSecurity={showSecurity}
            onShowSecurityChange={setShowSecurity}
            securityAnswer={securityAnswer}
            onSecurityAnswerChange={setSecurityAnswer}
            securityQuestionText={questionText}
          />
        </ThemedPanel>

        {msg && <ThemedNotice tone={msg.type === 'ok' ? 'success' : 'error'}>{msg.text}</ThemedNotice>}
      </main>

      <ThemedBottomActionBar>
        <ThemedSubmitButton
          onClick={handleSubmit}
          disabled={
            openMut.isPending ||
            !selection ||
            !txPassword.trim() ||
            previewQuery.isLoading ||
            previewQuery.isError ||
            !preview ||
            !preview.sufficient
          }
        >
          {openMut.isPending ? t('auth.loggingIn', 'Processing…') : payLabel}
        </ThemedSubmitButton>
      </ThemedBottomActionBar>
      </div>
    </div>
  );
}
